import React, { useRef, useState } from 'react';
import { HiArrowUp, HiCheck, HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import { chatCard, chatGhost, chatPrimary } from './chat-theme';
import CardHeader from './card-header';
import { USE_DEFAULTS_LINE } from '../model/clarification';

const Badge = ({ active, children }) => (
    <span
        aria-hidden="true"
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[11px] font-medium transition-colors ${active
            ? 'bg-gray-900 text-brand-lime dark:bg-brand-lime'
            : 'bg-slate-100 text-gray-500'}`}
    >
        {active ? <HiCheck className="h-3 w-3" /> : children}
    </span>
);

// Each choice is a white tile on the card's tint; the chosen one gets the brand's dark outline.
const rowClass = active => `flex cursor-pointer items-center gap-3 rounded-xl bg-white px-3 py-2.5 text-[0.875rem] transition-shadow peer-focus-visible:ring-2 peer-focus-visible:ring-lime-600 ${active
    ? 'text-gray-900 shadow-sm ring-2 ring-gray-900 dark:ring-brand-lime'
    : 'text-gray-700 ring-1 ring-slate-200 hover:ring-slate-300'}`;

/**
 * A question with numbered choices, each with an optional plain-language explanation, and an always-available custom answer; no options = free text.
 * A recommended choice is preselected, and "Use defaults for the rest" lets the copilot take its recommendation for every later question too.
 * Number keys pick a choice (the last number focuses the custom answer); Enter in the custom answer sends.
 */
export default function ClarificationCard({ request, onAnswer, disabled, questionShown }) {
    const freeText = !request.options.length;
    const recommended = request.options.findIndex(option => option.recommended);
    const [selected, setSelected] = useState(freeText ? 'other' : recommended >= 0 ? recommended : null);
    const [custom, setCustom] = useState('');
    const submitting = useRef(false);
    const customRef = useRef(null);
    const answer = selected === 'other' ? custom.trim() : request.options[selected]?.label || '';
    const name = `clarification-${request.id}`;

    const send = async (reply, useDefaults = false) => {
        if (!reply || disabled || submitting.current) return;
        submitting.current = true;
        try { await onAnswer(`${request.question}\n${reply}${useDefaults ? `\n\n${USE_DEFAULTS_LINE}` : ''}`); }
        finally { submitting.current = false; }
    };

    const submit = event => {
        event.preventDefault();
        send(answer);
    };

    const onKeyDown = event => {
        if (disabled || event.target === customRef.current || event.metaKey || event.ctrlKey || event.altKey) return;
        const number = Number(event.key);
        if (!Number.isInteger(number) || number < 1 || number > request.options.length + 1) return;
        event.preventDefault();
        if (number <= request.options.length) setSelected(number - 1);
        else customRef.current?.focus();
    };

    return (
        <form
            onSubmit={submit}
            onKeyDown={onKeyDown}
            className={chatCard('knowledge')}
        >
            <CardHeader icon={HiOutlineChatBubbleLeftRight} tone="knowledge" title={freeText ? 'Your answer' : 'Choose an option'}
                meta={freeText ? null : `${request.options.length + 1} choices`} />
            <fieldset disabled={disabled} className="disabled:opacity-60">
                <legend className={questionShown ? 'sr-only' : 'pb-2.5 text-[0.875rem] font-medium text-gray-900'}>
                    {request.question}
                </legend>
                <div className="space-y-1.5">
                    {request.options.map((option, index) => (
                        <label key={option.label} className="relative block">
                            <input type="radio" className="peer sr-only" aria-label={option.label} name={name}
                                aria-describedby={option.description ? `${name}-${index}-description` : undefined}
                                checked={selected === index} onChange={() => setSelected(index)} />
                            <span className={rowClass(selected === index)}>
                                <Badge active={selected === index}>{index + 1}</Badge>
                                <span className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                        {option.label}
                                        {option.recommended && (
                                            <span className="rounded-md bg-lime-100 px-1.5 py-px text-[10px] font-semibold text-lime-800 dark:bg-brand-lime/10 dark:text-brand-lime">
                                                Recommended
                                            </span>
                                        )}
                                    </span>
                                    {option.description && (
                                        <span id={`${name}-${index}-description`}
                                            className="mt-0.5 block text-[0.8rem] leading-snug text-gray-500">
                                            {option.description}
                                        </span>
                                    )}
                                </span>
                            </span>
                        </label>
                    ))}
                    <label className="relative block">
                        {!freeText && (
                            <input type="radio" className="peer sr-only" aria-label="Other" name={name}
                                checked={selected === 'other'} onChange={() => { setSelected('other'); customRef.current?.focus(); }} />
                        )}
                        <span className={rowClass(!freeText && selected === 'other')}>
                            {!freeText && <Badge active={selected === 'other' && Boolean(custom.trim())}>{request.options.length + 1}</Badge>}
                            <input
                                ref={customRef}
                                aria-label="Custom answer"
                                placeholder={freeText ? 'Type your answer…' : 'Something else…'}
                                value={custom}
                                maxLength={4000}
                                onFocus={() => setSelected('other')}
                                onChange={event => { setSelected('other'); setCustom(event.target.value); }}
                                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.875rem] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                            />
                        </span>
                    </label>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
                    <span className="text-[11px] text-gray-400">
                        {freeText ? 'Enter to send' : `Press 1–${request.options.length + 1} to choose`}
                    </span>
                    <span className="flex items-center gap-1">
                        {recommended >= 0 && (
                            <button
                                type="button"
                                onClick={() => send(answer || request.options[recommended].label, true)}
                                title="Answer this question and let the copilot pick the recommended option for any remaining questions"
                                className={chatGhost}
                            >
                                Use defaults for the rest
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={disabled || !answer}
                            className={chatPrimary}
                        >
                            Send answer <HiArrowUp className="h-3.5 w-3.5" />
                        </button>
                    </span>
                </div>
            </fieldset>
        </form>
    );
}
